import Vue from 'vue';
import moment from "moment";

import api from "@modules/AudienceManager/api";
import security from "@core/Util/security";
import {audienceStatus, questionTypes} from '@modules/AudienceManager/Util';
import {deepClone} from "@core/Util/clone";

const baseState = {
    /**
     * @type {String}
     */
    audienceName: '',

    /**
     * @type {String}
     */
    audienceDescription: '',
    
    /**
     * @type {AudienceStatus}
     */
    audienceStatus: audienceStatus.concept,

    /**
     * @type {boolean} Bit indicating whether current audience is yet to be created or not
     */
    isNewAudience: false,

    /**
     * @type {boolean} Bit indicating whether the audience was created via a file upload or not
     */
    isUploadedAudience: false,

    /**
     * @type {String}
     */
    audienceId: undefined,
    
    /**
     * @type {AudienceProgress}
     */
    audienceProgress: {
        total: undefined,
        goal: undefined,
        lastRetrievedUTC: new Date().toISOString()
    },

    /**
     * @type {CancellablePromise<AudienceProgress>}
     */
    audienceProgressPromise: undefined,

    /**
     * @type {Boolean} Bit indicating whether user has write permission for the audience builder module
     */
    hasWritePermission: false,

    /**
     * @type {Boolean} Boolean indicating whether audience progress is currently being loaded or not
     */
    isLoadingAudienceProgress: false,

    /**
     * @type {Object.<String,Object>} Object containing keyValuePairs of tabId/tab
     */
    tabs: {},

    /**
     * @type {Object.<String, Object>} Object containing keyValuePairs of categoryId/category
     */
    categories: {},
    
    /**
     * Labels belonging to values not defined alongside the question itself
     * @type {Object.<String, Object.<String, String>>} Object containing keyValuePairs of questionId/ value -> label
     */
    answerExternalValueLabels: {},
    
    /**
     * @type {Object.<string, string>} Object containing keyValuePairs of questionId/filter
     */
    answerFilters: {},

    /**
     * @type {Object.<string, string|Array<string>>} Object containing keyValuePairs of questionId/value
     */
    answerValues: {}
};

const cleanBaseState = deepClone(baseState);
const cloneBaseState = () => deepClone(cleanBaseState);

export default {
    namespaced: true,
    state: {...baseState, audienceProgress: { ...baseState.audienceProgress } },
    getters: {
        questions(state) {
            //Store in state?
            return Object.values(state.categories).reduce((map, category) => {
                category.questions.forEach(question => map[question.id] = question);
                return map;
            }, {});
        },
        /**
        * @param {Object} state Vuex state
        * @returns {Object.<string, string>|undefined}  Selected categories when present; otherwise undefined
        */
        categoriesValue(state) {
            return tabId => {
                return Object.values(state.tabs).reduce((map, tab) => {
                    if(!tabId || tab.id === tabId) {
                        tab.categories.forEach((category) => map[category.id] = category);
                    }
                    return map;
                }, {});
            };
        },
        categoryQuestions(state){
            return categoryId => {
                const category = state.categories[categoryId];
                return category && category.questions || [];
            };
        },
        questionFilterOptions(state, getters) {
            /**
             * @param {string} questionId Id of the question for which to look up available filters.
             * @returns {Array<{label: string, value: string}>|undefined} When present, an array of available options for the given question
             */
            return questionId => {
                const question = getters.questions[questionId];

                //Only MultiSelectFilter questions support FilterOptions for now
                if(!question || question.type !== questionTypes.MultiSelectFilter){
                    return [];
                }

                return question.options.values;
            };
        },
        questionOptions(state, getters){
            /**
             * @param {string} questionId Id of the question for which to look up available options.
             * @returns {Array<{label: string, value: string}>|undefined} When present, an array of available options for the given question
             */
            return questionId => {
                const question = getters.questions[questionId];

                if(!question){
                    return [];
                }
                
                //MultiSelectFilter questions have their actual selectable values provided via a remote endpoint
                if(question.type === questionTypes.MultiSelectFilter){
                    const externalValueLabels = state.answerExternalValueLabels[questionId] || {};
                    
                    return Object.keys(externalValueLabels).map(value => ({ 
                        label: externalValueLabels[value], 
                        value: value 
                    }));
                }

                return question.options.values;
            };
        },
        questionValue(state){
            /**
             * @param {string} questionId   Id of question for which to look up the value
             * @returns {string|Array<string>|undefined}  Selected value when present; otherwise undefined
             */
            return questionId => {
                return state.answerValues[questionId];
            };
        },
        questionFilterValue(state){
            /**
             * @param {string} questionId   Id of question for which to look up the filter value
             * @returns {string|undefined}  Selected filter value when present; otherwise undefined
             */
            return questionId => {
                return state.answerFilters[questionId];
            };
        },

        /**
         * @param {Object} state Vuex state
         * @returns {Object[]} Tabs sorted by index
         */
        sortedTabs(state) {
            return Object.values(state.tabs)
                .sort((l, r) => l.index > r.index ? 1 : -1);
        },

        nextTabId(state, getters) {
            const sortedTabs = getters.sortedTabs;

            /**
             * @param {string} tabId Tab id for which to retrieve the next tab id
             * @Returns {String|undefined} Returns the next tabId when available, otherwise undefined
             */
            return tabId => {
                const currentTabIndex = sortedTabs.indexOf(state.tabs[tabId]);
                const nextTab = sortedTabs[currentTabIndex + 1];
                return nextTab && nextTab.id;
            };
        },
        
        /**
         * When present; format the lastRetrieved from our audienceProgress object to a human readable format
         * @param {Object} state Vuex state
         * @returns {string|undefined} Formatted date.
         */
        audienceProgressLastRetrievedDisplay(state){
            //When our data was last retrieved today; we want to show only hour/minute
            //otherwise we should display the full date as to not confuse our users
            const todayDisplayFormat = 'HH:mm';
            const pastDateDisplayFormat = `YYYY-MM-DD ${todayDisplayFormat}`;

            if(!state.audienceProgress.lastRetrievedUTC){
                return undefined;
            }

            const momentLastRetrieved = moment.utc(state.audienceProgress.lastRetrievedUTC);
            const yesterday = moment.utc().subtract(1, 'days');

            const isToday = momentLastRetrieved.isAfter(yesterday, 'days');
            const displayFormat = isToday ? todayDisplayFormat : pastDateDisplayFormat;

            return momentLastRetrieved.local().format(displayFormat);
        },

        /**
         * Bit indicating whether audience can be edited or not
         * @param state Vuex state
         * @return {boolean}
         */
        canEditAudience(state){
            //Only audiences in concept state can be edited
            return state.hasWritePermission && state.audienceStatus === audienceStatus.concept;
        },

        /**
         * Bit indicating whether audience 'regular' question answers can be edited
         * @param {Object} state Vuex state
         * @param {Object} getters Vuex getters
         * @return {boolean}
         */
        canEditAudienceQuestionAnswers(state, getters){
            //An uploaded audience is expected to contain all necessary data for audience creation
            //therefor answers should not be allowed to be edited anymore
            return getters.canEditAudience && !state.isUploadedAudience;
        }
    },
    mutations: {
        /**
         *
         * @param {Object} state                Current store state.
         * @param {Object} context              Context for the mutation.
         * @param {string} context.questionId   Id of the question for which to set the value.
         * @param {string} context.value        Selected filter for the given question.
         */
        setQuestionFilterValue(state, context){
            const {questionId, value} = context;
            Vue.set(state.answerFilters, questionId, value);

            console.log(`Received: ${value} for ${questionId}`);
        },

        /**
         * Set the answer for the given question
         * @param {Object} state                Current store state.
         * @param {Object} context              Context for the mutation.
         * @param {string} context.questionId   Id of the question for which to set the value.
         * @param {string[]} context.value        Selected answer for the given question.
         */
        setQuestionValue(state, context){
            const {questionId, value} = context;
            Vue.set(state.answerValues, questionId, value);

            console.log(`Received: ${value} for ${questionId}`);
        },
        /**
         * Set the label for the given value for the given question
         * @param {Object} state Vuex state
         * @param {Object} context Mutation context
         * @param {String} context.questionId
         * @param {Array.<{ label: String, value: String }>} context.options
         */
        setExternalValueLabels(state, context){
            Vue.set(
                state.answerExternalValueLabels, 
                context.questionId,
                context.options.reduce((map, option) => {
                    map[option.value] = option.label;
                    return map;
                }, {})
            );
        },
        
        /**
         * Adds tabs to the state object.
         * @param {Object} state 
         * @param {Array.Object} tabs 
         */
        setTabs(state, {tabs}) {
            //Use Vue.set to make sure our tabs will be reactive.
            tabs.forEach((tab) => {
                Vue.set(state.tabs, tab.id, tab);

                //make categories available in state.
                tab.categories.forEach((category) => {
                    Vue.set(state.categories, category.id, category);
                });
            });
        },

        /**
         * Set progress for the current audience
         * @param {Object} state Current Vuex state.
         * @param {AudienceProgress} progress Object containing audience progress.
         */
        setProgress(state, progress){
            state.audienceProgress = progress;
        },

        /**
         * Mark progress loader started
         * @param state
         */
        markStartLoadingProgress(state){
            state.isLoadingAudienceProgress = true;
        },

        /**
         * Mark progress loader finished
         * @param state
         */
        markEndLoadingProgress(state){
            state.isLoadingAudienceProgress = false;
        },

        /**
         * Set progress promise for current audience
         * @param {Object} state Current Vuex state.
         * @param {Promise<AudienceProgress>} progressPromise
         */
        setProgressPromise(state, progressPromise){
            state.audienceProgressPromise = progressPromise;
        },

        /**
         * Set the current audience
         * @param state
         * @param {{id: String, audience: AudienceEditModel}} payload
         */
        setAudience(state, {id, audience}){
            state.audienceId = id;
            state.audienceStatus = audience.status;
            
            const {
                answerValues,
                answerFilters,
                answerExternalValueLabels
            } = audience.answers.reduce((selection, answer) => {
                selection.answerValues[answer.questionId] = answer.value;
                
                if(answer.metadata){
                    if(answer.metadata.selectedFilter){
                        selection.answerFilters[answer.questionId] = answer.metadata.selectedFilter;
                    }
                    
                    if(answer.metadata.valueLabels){
                        selection.answerExternalValueLabels[answer.questionId] = answer.metadata.valueLabels;
                    }
                }
                
                return selection;
            }, { answerValues: {}, answerFilters: {}, answerExternalValueLabels: {} });

            state.audienceName = audience.name;
            state.audienceDescription = audience.description;
            state.answerValues = answerValues;
            state.answerFilters = answerFilters;
            state.answerExternalValueLabels = answerExternalValueLabels;
        },
        
        /**
         * Set the status for the current audience
         * @param {Object} state Vuex state
         * @param {AudienceStatus} status
         */
        setAudienceStatus(state, status){
            state.audienceStatus = status;
        },

        /**
         * Toggle whether the audience is marked as new or not
         * @param {Object} state Vuex state
         * @param {boolean} isNewAudience
         */
        setNewAudienceMarker(state, isNewAudience){
            state.isNewAudience = isNewAudience;
        },

        /**
         * Toggle whether the audience is marked as created from upload or not
         * @param {Object} state Vuex state
         * @param {boolean} isUpload
         */
        setUploadedAudienceMarker(state, isUpload){
            state.isUploadedAudience = isUpload;
        },

        /**
         * Set the name for the given audience
         * @param {Object} state Vuex state
         * @param {String} name Audience name
         */
        setAudienceName(state, name){
            state.audienceName = name;
        },

        /**
         * Set whether the current user has write permissions for the audience builder
         * @param {Object} state Vuex state
         * @param {boolean} hasWritePermission Bit indicating whether user has permission or not
         */
        setWritePermission(state, hasWritePermission){
            state.hasWritePermission = hasWritePermission;
        },
        
        /**
         * Perform a full reset on Vuex state
         * @param {Object} state
         */
        resetStoreState(state) {
            const baseStateClone = cloneBaseState();
            Reflect.ownKeys(baseStateClone).forEach(stateKey => state[stateKey] = baseStateClone[stateKey]);
        }
    },
    actions: {
        /**
         * Load the tabs, ensuring availability for future actions
         * 
         * @param {function} commit Commit vuex state.
         * @param {Object} state    Object containing Vuex state.
         */
        loadTabs({commit, state}) {
            //Check if tabs have already been loaded
            if(state.tabs.length > 0) {
                return Promise.resolve();
            }

            //We want to wrap our API call in a promise to prevent leaking
            //details of the api call taking place
            return new Promise((resolve, reject) => {
                api.getTabs().then(
                    tabs => {
                        commit('setTabs', {tabs});

                        //Notify caller about completion.
                        resolve();
                    },
                    reject
                );
            });
        }, 
        
        /**
         * Load progress for the current audience
         * @param {Function} commit Vuex commit handler.
         * @param {Function} dispatch Vuex dispatch handler.
         * @param {Object} state Vuex state.
         * @param {Object} getters Vuex getters
         * @returns {Promise<void>} Promise indicating success/failure of the action
         */
        async loadProgress({commit, dispatch, state, getters}){
            const prevProm = state.audienceProgressPromise;
            
            if(prevProm){
                prevProm.cancel();
            }
            
            commit('markStartLoadingProgress');

            //An audience only needs to be saved when it can actually be edited
            //eg. published audiences still need to be viewable in the builder
            if(getters.canEditAudience){
                //In order for us to determine the current audience progress, we'll need an actual saved audience
                //saving our audience in the background should be the easiest option for now
                try{
                    await dispatch('saveAudience');
                } catch (e) {
                    commit('markEndLoadingProgress');
                    throw e;
                }   
            }
            
            const prom = api.getProgress(state.audienceId);
            
            commit('setProgressPromise', prom);
            
            //When our call fails, we still want to invoke setProgress
            //to notify us being 'done' loading audience progress
            let progress = state.audienceProgress;
            try {
                progress = await prom;
            } finally {
                commit('setProgress',  progress);
                commit('markEndLoadingProgress');
            }
        },

        /**
         * Load the audience with the given id for edit purposes
         * @param commit Vuex commit handler
         * @param dispatch Vuex dispatch handler
         * @param {String} id Audience id
         * @return {Promise<void>}
         */
        async loadAudience({commit, dispatch}, id){
            const audience = await api.getBuilderAudience(id);

            await dispatch('loadPermissions');

            commit('setNewAudienceMarker', false);
            commit('setAudience', {
                id: id,
                audience: audience
            });
            commit('setUploadedAudienceMarker', audience.isUpload);
        },

        /**
         * Load a new audience with the given id for edit purposes
         * @param commit Vuex commit handler
         * @param dispatch Vuex dispatch handler
         * @param {String} id New audience id
         * @return {Promise<void>}
         */
        async loadNewAudience({commit, dispatch}, id){
            await dispatch('loadPermissions');
            
            commit('setNewAudienceMarker', true);
            commit('setAudience', {
                id: id,
                audience: {
                    name: '',
                    description: '',
                    status: audienceStatus.concept,
                    answers: []
                }
            });
        },

        /**
         * Create an audience based on the given CSV file
         * @param commit Vuex commit handler
         * @param state Vuex state
         * @param {File} csvFile CSV file to base audience on
         * @return {Promise<void>}
         */
        async createAudienceFromCsv({commit, state}, csvFile){
            const audience = await api.createAudienceFromCsv(
                state.audienceId, 
                csvFile,
                state.audienceName
            );
            
            commit('setNewAudienceMarker', false);
            commit('setUploadedAudienceMarker', true);
            commit('setAudience', {
                id: state.audienceId,
                audience: audience
            });
        },

        /**
         * Save the current audience
         * @param commit Vuex commit handler
         * @param state Vuex state
         * @return {Promise<void>}
         */
        async saveAudience({commit, state}){
            /**
             * @type {AudienceEditModel}
             */
            const model = {
                name: state.audienceName,
                description: state.audienceDescription,
                status: state.audienceStatus,
                answers: Object.keys(state.answerValues).map(questionId => {
                    const metadata = {};
                    
                    if(state.answerFilters[questionId]){
                        metadata.selectedFilter = state.answerFilters[questionId];
                    }
                    
                    if(state.answerExternalValueLabels[questionId]){
                        metadata.valueLabels = state.answerExternalValueLabels[questionId];
                    }
                    
                    return {
                        questionId: questionId,
                        value: state.answerValues[questionId],
                        metadata: metadata
                    };
                })
            };
            
            if(state.isNewAudience){
                await api.createAudience(state.audienceId, model);
                commit('setNewAudienceMarker', false);
            } else {
                await api.updateAudience(state.audienceId, model);
            }
        },

        /**
         * Publish the current audience
         * @param commit Vuex commit handler
         * @param dispatch Vuex dispatch handler
         * @return {Promise<void>}
         */
        async publishAudience({commit, dispatch}){
            commit('setAudienceStatus', audienceStatus.published);
            
            return await dispatch('saveAudience');
        },

        async loadPermissions({commit}){
            try {
                const hasPermission = await security.validatePermission(
                    security.Permissions.AudienceManager,
                    security.PermissionGrantTypes.Write
                );

                console.log('Setting write permission true');
                commit('setWritePermission', hasPermission);
            } catch (e) {
                console.log('Setting write permission false', e);
                commit('setWritePermission', false);
            }
        },
        
        /**
         * Reset the store to an empty state, this is necessary to prevent hogging memory upon leaving the module
         */
        cleanup({commit, state}){
            //TODO: Implement
            //Call when navigating away from module
            //Invoke mutations cleaning state not needed by other modules
            //this to prevent memory leaks from taking place 

            //https://tahazsh.com/vuebyte-reset-module-state
            //https://stackoverflow.com/questions/42295340/how-to-clear-state-in-vuex-store
            commit('resetStoreState');
        }
    }
};